"use client";

import { useTranslations } from "next-intl";
import { Project } from "@/data/projects";
import Image from "next/image";

interface ProjectCardProps {
  project: Project;
}

export const ProjectCard = ({ project }: ProjectCardProps) => {
  const t = useTranslations("projects.card");
  const tData = useTranslations("projects.data");

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-lg bg-tertiary lg:h-[370px] lg:flex-row">
      {/* Top/Left side - Mockup Image */}
      {project.mockupDesktopImage && (
        <div className="relative w-full p-[8px] lg:w-[48%]">
          <div className="relative h-64 w-full lg:h-full">
            <Image
              src={project.mockupDesktopImage}
              alt={`${t("mockupAlt")} ${project.name}`}
              fill
              className="rounded-lg object-cover"
            />
          </div>
        </div>
      )}

      {/* Bottom/Right side - Content */}
      <div className="flex w-full flex-col justify-center gap-4 p-8 lg:w-[52%]">
        {project.projectLogo && (
          <div className="mb-4 flex justify-start">
            <Image
              src={project.projectLogo}
              alt={`${t("logoAlt")} ${project.name}`}
              width={200}
              height={30}
              className="h-[30px] w-auto object-contain lg:h-[36px]"
            />
          </div>
        )}
        <div className="flex flex-col gap-2">
          {project.translationKey &&
            tData(`${project.translationKey}.comment`) && (
              <blockquote className="text-lg italic leading-relaxed text-[#454545]">
                &quot;{tData(`${project.translationKey}.comment`)}&quot;
              </blockquote>
            )}

          {project.translationKey &&
            tData(`${project.translationKey}.author`) && (
              <p className="text-sm text-accent">
                {tData(`${project.translationKey}.author`)}
              </p>
            )}
        </div>

        {/* URL and Project Data */}
        <div className="flex flex-col-reverse items-start justify-between gap-6 pt-4 sm:flex-row sm:items-center">
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-block overflow-hidden text-[#454545]"
          >
            <span className="relative z-10">{t("viewSite")}</span>
            <span className="absolute bottom-0 left-0 h-px w-full bg-[#454545] transition-all duration-300 group-hover:opacity-0"></span>
            <span className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-[#454545] opacity-0 transition-transform duration-700 ease-in-out group-hover:scale-x-100 group-hover:opacity-100 group-[.unhover]:origin-right group-[.unhover]:scale-x-0 group-[.unhover]:opacity-0"></span>
          </a>
          {project.translationKey &&
            tData(`${project.translationKey}.projectData`) && (
              <div className="rounded-lg bg-primary p-[12px] text-sm text-secondary transition-all duration-300 ease-in-out md:text-base">
                {tData(`${project.translationKey}.projectData`)}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
